import { useReducer, useCallback, useEffect, useRef } from 'react';
import { ChatState, ChatAction, ChatMessage, ChatConversation, UseChatReturn } from '../types/chat';
import { chatApiService } from '../services/chatApiService';
import { generateId } from '../utils/generateId';

// Configuration du streaming
const STREAMING_CONFIG = {
  CHARS_PER_UPDATE: 3,        // Nombre de caractères par mise à jour
  UPDATE_DELAY_MS: 30,        // Délai entre chaque mise à jour (ms)
  MIN_DELAY_MS: 20,           // Délai minimum
  MAX_DELAY_MS: 50,           // Délai maximum
};

// Fonction utilitaire pour nettoyer les timestamps
const ensureValidDate = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  
  if (timestamp instanceof Date) {
    return isNaN(timestamp.getTime()) ? new Date() : timestamp;
  }
  
  try {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? new Date() : date;
  } catch {
    return new Date();
  }
};

// Fonction pour nettoyer un message
const sanitizeMessage = (message: any): ChatMessage => {
  return {
    ...message,
    timestamp: ensureValidDate(message.timestamp),
    id: message.id || generateId(),
    content: message.content || '',
    isBot: Boolean(message.isBot),
    status: message.status || 'sent',
  };
};

// Fonction pour nettoyer une conversation
const sanitizeConversation = (conversation: any): ChatConversation => {
  return {
    ...conversation,
    id: conversation.id || generateId(),
    messages: (conversation.messages || []).map(sanitizeMessage),
    isActive: Boolean(conversation.isActive),
    startedAt: ensureValidDate(conversation.startedAt),
    lastActivity: ensureValidDate(conversation.lastActivity),
    metadata: conversation.metadata || {},
  };
};

// État initial du chat
const initialChatState: ChatState = {
  isOpen: false,
  isMinimized: false,
  isTyping: false,
  isConnected: false,
  currentConversation: null,
  conversations: [],
  unreadCount: 0,
  error: undefined,
};

// Reducer pour gérer l'état du chat
function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'TOGGLE_CHAT':
      return {
        ...state,
        isOpen: !state.isOpen,
        isMinimized: false,
        unreadCount: state.isOpen ? state.unreadCount : 0,
      };

    case 'OPEN_CHAT':
      return {
        ...state,
        isOpen: true,
        isMinimized: false,
        unreadCount: 0,
      };

    case 'CLOSE_CHAT':
      return {
        ...state,
        isOpen: false,
        isMinimized: false,
      };

    case 'MINIMIZE_CHAT':
      return {
        ...state,
        isMinimized: !state.isMinimized,
      };

    case 'START_TYPING':
      return {
        ...state,
        isTyping: true,
      };

    case 'STOP_TYPING':
      return {
        ...state,
        isTyping: false,
      };

    case 'ADD_MESSAGE':
      const newMessage = sanitizeMessage({
        ...action.payload,
        timestamp: ensureValidDate(action.payload.timestamp),
      });
      const currentConv = state.currentConversation;
      
      if (!currentConv) {
        const newConversation: ChatConversation = {
          id: generateId(),
          messages: [newMessage],
          isActive: true,
          startedAt: new Date(),
          lastActivity: new Date(),
          metadata: {
            sessionId: generateId(),
          },
        };

        return {
          ...state,
          currentConversation: newConversation,
          conversations: [newConversation, ...state.conversations.slice(0, 9)], // Garde max 10 conversations
          unreadCount: !state.isOpen && !newMessage.isBot ? state.unreadCount + 1 : state.unreadCount,
        };
      }

      const updatedConversation: ChatConversation = {
        ...currentConv,
        messages: [...currentConv.messages, newMessage],
        lastActivity: new Date(),
      };

      return {
        ...state,
        currentConversation: updatedConversation,
        conversations: state.conversations.map(conv => 
          conv.id === currentConv.id ? updatedConversation : conv
        ),
        unreadCount: !state.isOpen && !newMessage.isBot ? state.unreadCount + 1 : state.unreadCount,
      };

    case 'UPDATE_MESSAGE':
      if (!state.currentConversation) return state;

      const updatedMessages = state.currentConversation.messages.map(msg =>
        msg.id === action.payload.id ? { ...msg, ...action.payload.updates } : msg
      );

      const updatedCurrentConv = {
        ...state.currentConversation,
        messages: updatedMessages,
      };

      return {
        ...state,
        currentConversation: updatedCurrentConv,
        conversations: state.conversations.map(conv =>
          conv.id === state.currentConversation!.id ? updatedCurrentConv : conv
        ),
      };

    case 'SET_CONVERSATION':
      const sanitizedConversation = action.payload ? sanitizeConversation(action.payload) : null;
      return {
        ...state,
        currentConversation: sanitizedConversation,
      };

    case 'RESET_CONVERSATION':
      return {
        ...state,
        currentConversation: null,
        error: undefined,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isTyping: false,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: undefined,
      };

    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        isConnected: action.payload,
      };

    default:
      return state;
  }
}

/**
 * Hook personnalisé pour gérer l'état et les interactions du chat Adha
 * 
 * @description Centralise la logique du chat : état, envoi de messages, historique
 * @returns Interface complète pour interagir avec le chat
 */
export function useChat(): UseChatReturn {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);

  // Nettoyage initial des données
  useEffect(() => {
    try {
      // Vérifier et diagnostiquer les données
      const chatData = localStorage.getItem('adha_chat_data');
      if (chatData) {
        const parsed = JSON.parse(chatData);
        
        console.log('🤖 Chat Adha: Vérification des données existantes...');
        
        // Vérifier si les données semblent corrompues
        let hasCorruptedData = false;
        
        if (parsed.currentConversation?.messages) {
          for (const msg of parsed.currentConversation.messages) {
            if (msg.timestamp && !(msg.timestamp instanceof Date) && isNaN(new Date(msg.timestamp).getTime())) {
              hasCorruptedData = true;
              break;
            }
          }
        }
        
        if (hasCorruptedData) {
          console.warn('⚠️ Données corrompues détectées! Tapez resetChatData() dans la console pour nettoyer');
          dispatch({ type: 'SET_ERROR', payload: 'Données corrompues détectées. Utilisez resetChatData() pour nettoyer.' });
        } else {
          // Nettoyer la conversation courante si elle existe
          if (parsed.currentConversation) {
            const cleanedConversation = sanitizeConversation(parsed.currentConversation);
            dispatch({ type: 'SET_CONVERSATION', payload: cleanedConversation });
          }
        }
      }
    } catch (error) {
      console.warn('❌ Erreur lors du chargement des données chat:', error);
      console.log('🧹 Tapez resetChatData() dans la console pour nettoyer');
      localStorage.removeItem('adha_chat_data');
    }
  }, []);

  // Actions du chat
  // Test de connexion initial
  useEffect(() => {
    const testConnection = async () => {
      try {
        const isConnected = await chatApiService.checkConnection();
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: isConnected });
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`🤖 Chat Adha: ${isConnected ? '✅ Connecté' : '❌ Mode dégradé'}`);
        }
      } catch (error) {
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
        console.warn('Test de connexion chat échoué:', error);
      }
    };

    testConnection();
    
    // Test périodique de connexion (toutes les 5 minutes)
    const interval = setInterval(testConnection, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const toggleChat = useCallback(() => {
    dispatch({ type: 'TOGGLE_CHAT' });
  }, []);

  const openChat = useCallback(() => {
    dispatch({ type: 'OPEN_CHAT' });
  }, []);

  const closeChat = useCallback(() => {
    dispatch({ type: 'CLOSE_CHAT' });
  }, []);

  const minimizeChat = useCallback(() => {
    dispatch({ type: 'MINIMIZE_CHAT' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const resetConversation = useCallback(() => {
    // Annuler toute requête en cours
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    dispatch({ type: 'RESET_CONVERSATION' });
    
    // Analytics
    if (typeof window !== 'undefined') {
      console.log('Chat conversation reset');
    }
  }, []);

  // Envoi de message avec gestion complète
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoadingRef.current) return;

    isLoadingRef.current = true;
    dispatch({ type: 'CLEAR_ERROR' });

    // Annuler toute requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Créer le message utilisateur
    const userMessage: ChatMessage = {
      id: generateId(),
      content: content.trim(),
      isBot: false,
      timestamp: new Date(),
      status: 'sending',
    };

    // Ajouter le message utilisateur
    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });

    // Marquer comme envoyé
    dispatch({ 
      type: 'UPDATE_MESSAGE', 
      payload: { 
        id: userMessage.id, 
        updates: { status: 'sent' } 
      }
    });

    // Créer un message bot vide pour le streaming
    const botMessageId = generateId();
    const botMessage: ChatMessage = {
      id: botMessageId,
      content: '',
      isBot: true,
      timestamp: new Date(),
      status: 'sending',
    };

    // Buffer pour le streaming progressif
    let streamBuffer: string[] = [];
    let accumulatedContent = '';
    let streamingIntervalId: NodeJS.Timeout | null = null;

    // Fonction pour afficher progressivement le contenu bufferisé
    const processStreamBuffer = () => {
      if (streamBuffer.length === 0) return;

      // Prendre plusieurs caractères à la fois pour un effet naturel
      const charsToAdd = streamBuffer.splice(0, STREAMING_CONFIG.CHARS_PER_UPDATE).join('');
      accumulatedContent += charsToAdd;

      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: {
          id: botMessageId,
          updates: {
            content: accumulatedContent,
          },
        },
      });
    };

    // Simuler la frappe du bot
    dispatch({ type: 'START_TYPING' });

    try {
      // Préparer l'historique de la conversation
      const conversationHistory = state.currentConversation?.messages || [];
      
      // Ajouter le message bot vide
      dispatch({ type: 'ADD_MESSAGE', payload: botMessage });

      // Démarrer le traitement progressif du buffer
      streamingIntervalId = setInterval(processStreamBuffer, STREAMING_CONFIG.UPDATE_DELAY_MS);
      
      // Appel API avec streaming
      const response = await chatApiService.sendMessage({
        content: content.trim(),
        conversationHistory: [...conversationHistory, userMessage],
        context: {
          timestamp: new Date().toISOString(),
          sessionId: state.currentConversation?.metadata?.sessionId,
        },
        onStream: (chunk: string) => {
          // Ajouter chaque caractère au buffer
          streamBuffer.push(...chunk.split(''));
        },
      }, abortControllerRef.current.signal);

      // Attendre que le buffer soit vidé
      const waitForBufferEmpty = new Promise<void>((resolve) => {
        const checkBuffer = setInterval(() => {
          if (streamBuffer.length === 0) {
            clearInterval(checkBuffer);
            if (streamingIntervalId) {
              clearInterval(streamingIntervalId);
            }
            resolve();
          }
        }, 50);
      });

      await waitForBufferEmpty;

      if (response.success) {
        // Marquer le message comme envoyé avec les métadonnées finales
        dispatch({ 
          type: 'UPDATE_MESSAGE', 
          payload: { 
            id: botMessageId, 
            updates: { 
              status: 'sent',
              content: response.message || accumulatedContent,
              metadata: {
                conversationId: response.conversationId,
                ...response.metadata,
              },
            } 
          }
        });
      } else {
        throw new Error(response.error || 'Erreur de communication avec l\'assistant');
      }
    } catch (error: any) {
      console.error('Erreur envoi message chat:', error);
      
      // Nettoyer l'intervalle en cas d'erreur
      if (streamingIntervalId) {
        clearInterval(streamingIntervalId);
      }
      
      if (error.name !== 'AbortError') {
        const errorMessage = error.message || 'Une erreur est survenue. Veuillez réessayer.';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });

        // Marquer le message utilisateur comme erreur
        dispatch({ 
          type: 'UPDATE_MESSAGE', 
          payload: { 
            id: userMessage.id, 
            updates: { status: 'error' } 
          }
        });

        // Supprimer ou marquer le message bot comme erreur
        dispatch({ 
          type: 'UPDATE_MESSAGE', 
          payload: { 
            id: botMessageId, 
            updates: { status: 'error', content: errorMessage } 
          }
        });
      }
    } finally {
      dispatch({ type: 'STOP_TYPING' });
      isLoadingRef.current = false;
      abortControllerRef.current = null;
    }
  }, [state.currentConversation]);

  // Gestion de la connexion
  useEffect(() => {
    let mounted = true;

    const checkConnection = async () => {
      try {
        const isConnected = await chatApiService.checkConnection();
        if (mounted) {
          dispatch({ type: 'SET_CONNECTION_STATUS', payload: isConnected });
        }
      } catch (error) {
        if (mounted) {
          dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
        }
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 30000); // Vérifier toutes les 30s

    return () => {
      mounted = false;
      clearInterval(interval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Persistence locale (optionnelle)
  useEffect(() => {
    if (state.currentConversation && typeof window !== 'undefined') {
      try {
        localStorage.setItem('adha_chat_conversation', JSON.stringify(state.currentConversation));
      } catch (error) {
        console.warn('Impossible de sauvegarder la conversation:', error);
      }
    }
  }, [state.currentConversation]);

  // Restaurer la conversation au chargement
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('adha_chat_conversation');
        if (saved) {
          const conversation = JSON.parse(saved);
          dispatch({ type: 'SET_CONVERSATION', payload: conversation });
        }
      } catch (error) {
        console.warn('Impossible de restaurer la conversation:', error);
      }
    }
  }, []);

  return {
    state,
    toggleChat,
    openChat,
    closeChat,
    minimizeChat,
    sendMessage,
    resetConversation,
    isLoading: isLoadingRef.current,
    error: state.error || null,
    clearError,
  };
}